import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { extractErr, API } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Facebook, Copy, Check, Loader2, Trash2, Download, MessageCircle } from "lucide-react";

export default function SuperFacebook() {
  const [sp, setSp] = useSearchParams();
  const [st, setSt] = useState(null);
  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [verify, setVerify] = useState("");
  const [pageId, setPageId] = useState("");
  const [pageName, setPageName] = useState("");
  const [pageToken, setPageToken] = useState("");
  const [rr, setRr] = useState([]);
  const [pages, setPages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [forms, setForms] = useState([]);
  const [formId, setFormId] = useState("");
  const [importing, setImporting] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [wa, setWa] = useState({ phone_number_id: "", access_token: "", waba_id: "", display_phone: "" });
  const webhook = useMemo(() => `${API}/public/meta/webhook`, []);

  const load = () => api.get("/platform/facebook/status").then(({ data }) => {
    setSt(data);
    setAppId(data.app_id || "");
    setVerify(data.verify_token || "");
    setPageId(data.page_id || "");
    setPageName(data.page_name || "");
    setRr(data.round_robin || []);
    if ((data.sync_form_ids || [])[0]) setFormId(data.sync_form_ids[0]);
    const w = data.whatsapp || {};
    setWa((s) => ({
      ...s,
      phone_number_id: w.phone_number_id || "",
      waba_id: w.waba_id || "",
      display_phone: w.display_phone || "",
    }));
  }).catch((e) => toast.error(extractErr(e)));

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const code = sp.get("code");
    if (!code) return;
    const redirect = `${window.location.origin}/super-admin/facebook`;
    api.post("/platform/facebook/oauth", { code, redirect_uri: redirect })
      .then(({ data }) => {
        setPages(data.pages || []);
        toast.success("Choose a Page below to finish connecting.");
      })
      .catch((e) => toast.error(extractErr(e)))
      .finally(() => {
        sp.delete("code"); sp.delete("state");
        setSp(sp, { replace: true });
      });
  }, [sp, setSp]);

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Copy failed"); }
  };

  const saveApp = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/platform/facebook/app", { app_id: appId, app_secret: appSecret, verify_token: verify });
      setSt(data); setVerify(data.verify_token); setAppSecret("");
      toast.success("App credentials saved");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };

  const startOauth = async () => {
    try {
      const redirect = `${window.location.origin}/super-admin/facebook`;
      const { data } = await api.get("/platform/facebook/oauth-url", { params: { redirect_uri: redirect } });
      window.location.href = data.url;
    } catch (e) { toast.error(extractErr(e)); }
  };

  const pickPage = async (p) => {
    setSaving(true);
    try {
      const { data } = await api.put("/platform/facebook/page", { page_id: p.id, page_name: p.name, page_access_token: p.access_token });
      setSt(data); setPages([]); setPageId(data.page_id); setPageName(data.page_name);
      toast.success(`Connected ${p.name} and subscribed leadgen`);
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };

  const saveManualPage = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/platform/facebook/page", { page_id: pageId, page_name: pageName, page_access_token: pageToken });
      setSt(data); setPageToken("");
      toast.success("Page connected");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };

  const saveRr = async () => {
    try {
      await api.put("/platform/facebook/round-robin", { sales_user_ids: rr });
      toast.success("Round-robin group saved");
    } catch (e) { toast.error(extractErr(e)); }
  };

  const toggleRr = (id) => setRr((xs) => xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]);

  const loadForms = async () => {
    try {
      const { data } = await api.get("/platform/facebook/forms");
      setForms(data.items || []);
      if (!formId && (data.sync_form_ids || [])[0]) setFormId(data.sync_form_ids[0]);
      toast.success(`${(data.items || []).length} Instant Form${(data.items || []).length === 1 ? "" : "s"} loaded`);
    } catch (e) { toast.error(extractErr(e)); }
  };

  const importForm = async () => {
    if (!formId) { toast.error("Select an Instant Form"); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const { data } = await api.post("/platform/facebook/forms/import", { form_id: formId, sync_future: true });
      setImportResult(data);
      toast.success(`Imported ${data.created} new · filled ${data.updated} · skipped ${data.duplicates} duplicates`);
      load();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setImporting(false); }
  };

  const cleanupJunk = async () => {
    if (!window.confirm("Delete blank CRM cards with no name, phone, email, or form answers? Sales inboxes will drop those junk rows. Real leads are kept.")) return;
    setCleaning(true);
    try {
      const { data } = await api.post("/platform/facebook/cleanup-junk", { confirm: true });
      toast.success(`Removed ${data.deleted} blank leads · ${data.remaining} remaining`);
      load();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setCleaning(false); }
  };

  const saveWa = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/platform/facebook/whatsapp", wa);
      setSt(data);
      setWa((s) => ({ ...s, access_token: "" }));
      toast.success("Team WhatsApp saved — all sales users share this number");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-100 flex items-center gap-2">
          <Facebook className="w-7 h-7 text-blue-400" /> Facebook Setup
        </h1>
        <p className="text-slate-400 text-sm mt-1">Connect a Page to receive Lead Form ads, then rotate new leads across Sales panel members.</p>
      </div>

      <Card className="bg-slate-900 border-slate-800 p-5 text-slate-200 space-y-3" data-testid="fb-tutorial">
        <div className="font-semibold text-slate-100">Setup tutorial (use your existing Meta app)</div>
        <ol className="list-decimal pl-5 text-sm text-slate-400 space-y-2">
          <li>Open <a className="text-blue-300 underline" href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer">developers.facebook.com/apps</a> and select the app under the same Business Manager (e.g. Grocode Ad live).</li>
          <li>App settings → Basic: copy <strong className="text-slate-200">App ID</strong> and <strong className="text-slate-200">App Secret</strong> into the form below and Save.</li>
          <li>Use cases → Customize → Webhooks: callback URL = <code className="text-teal-300 break-all">{webhook}</code> and verify token = the token saved here. Subscribe the <code className="text-teal-300">leadgen</code> field (you already have this on).</li>
          <li>Facebook Login for Business → Settings: add Valid OAuth Redirect URI <code className="text-teal-300 break-all">{typeof window !== "undefined" ? `${window.location.origin}/super-admin/facebook` : "/super-admin/facebook"}</code>.</li>
          <li>Permissions already approved that we use: <code>pages_show_list</code>, <code>business_management</code>, <code>ads_read</code>, <code>ads_management</code>, <code>pages_read_engagement</code>. Also request <code>leads_retrieval</code> and <code>pages_manage_metadata</code> if lead download or Page subscribe fails.</li>
          <li>Click <strong className="text-slate-200">Connect Facebook Page</strong>, pick the Page that runs the lead form ads. We subscribe that Page to <code>leadgen</code>.</li>
          <li>Select Sales members for round-robin. New Facebook (and new WhatsApp) leads rotate through that list and get a tag like <em>Manish&apos;s client</em>. Each sales login only sees their own assigned leads.</li>
          <li>Instant Forms: keep Meta&apos;s built-in <strong className="text-slate-200">Full name</strong>, <strong className="text-slate-200">Email</strong>, and <strong className="text-slate-200">Phone number</strong> questions (any extra questions are stored and shown automatically — no code change needed for new forms).</li>
          <li>Link the team WhatsApp Cloud API number here (section 6). Sales users send from that one number and only see their tagged chats.</li>
        </ol>
        <Button type="button" variant="outline" className="border-slate-600 text-slate-200" onClick={() => copy(webhook)}><Copy className="w-4 h-4 mr-1" /> Copy webhook URL</Button>
      </Card>

      <Card className="bg-slate-900 border-slate-800 p-5 text-slate-100 space-y-4" data-testid="fb-app-form">
        <div className="font-semibold">1. Meta app credentials</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>App ID</Label>
            <Input className="mt-1 bg-slate-950 border-slate-700" value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="1049…" data-testid="fb-app-id" />
          </div>
          <div>
            <Label>App Secret</Label>
            <Input type="password" className="mt-1 bg-slate-950 border-slate-700" value={appSecret} onChange={(e) => setAppSecret(e.target.value)} placeholder={st?.app_secret_set ? "saved — paste to rotate" : "secret"} data-testid="fb-app-secret" />
          </div>
        </div>
        <div>
          <Label>Webhook verify token</Label>
          <div className="mt-1 flex gap-2">
            <Input className="bg-slate-950 border-slate-700 font-mono-nums" value={verify} onChange={(e) => setVerify(e.target.value)} data-testid="fb-verify-token" />
            <Button type="button" variant="outline" className="border-slate-600" onClick={() => copy(verify)}>Copy</Button>
          </div>
        </div>
        <Button onClick={saveApp} disabled={saving} className="bg-blue-600 hover:bg-blue-500">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save app"}</Button>
      </Card>

      <Card className="bg-slate-900 border-slate-800 p-5 text-slate-100 space-y-4" data-testid="fb-page-form">
        <div className="font-semibold">2. Connect Page</div>
        {st?.page_id && (
          <p className="text-sm text-emerald-300 flex items-center gap-2"><Check className="w-4 h-4" /> Connected: {st.page_name} ({st.page_id})</p>
        )}
        <Button type="button" onClick={startOauth} className="bg-blue-600 hover:bg-blue-500" data-testid="fb-oauth">Connect Facebook Page</Button>
        {pages.length > 0 && (
          <div className="space-y-2">
            {pages.map((p) => (
              <button type="button" key={p.id} onClick={() => pickPage(p)} className="w-full text-left rounded-lg border border-slate-700 px-3 py-2 hover:bg-slate-800" data-testid={`fb-page-${p.id}`}>
                {p.name} <span className="text-slate-500 text-xs">{p.id}</span>
              </button>
            ))}
          </div>
        )}
        <p className="text-xs text-slate-500">Or paste a Page access token from Graph API Explorer / Business Settings.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Page ID</Label>
            <Input className="mt-1 bg-slate-950 border-slate-700" value={pageId} onChange={(e) => setPageId(e.target.value)} />
          </div>
          <div>
            <Label>Page name</Label>
            <Input className="mt-1 bg-slate-950 border-slate-700" value={pageName} onChange={(e) => setPageName(e.target.value)} />
          </div>
        </div>
        <div>
          <Label>Page access token</Label>
          <Input type="password" className="mt-1 bg-slate-950 border-slate-700" value={pageToken} onChange={(e) => setPageToken(e.target.value)} />
        </div>
        <Button type="button" variant="outline" className="border-slate-600" disabled={saving} onClick={saveManualPage}>Save Page token</Button>
      </Card>

      <Card className="bg-slate-900 border-slate-800 p-5 text-slate-100 space-y-4" data-testid="fb-rr-form">
        <div className="font-semibold">3. Round-robin lead distribution</div>
        <p className="text-sm text-slate-400">Leads rotate through the checked Sales users (created under Sales users). Order is the order you tick them.</p>
        <div className="space-y-2">
          {(st?.sales_users || []).length === 0 && <p className="text-sm text-slate-500">Create sales logins first at Sales users.</p>}
          {(st?.sales_users || []).map((u) => (
            <label key={u.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={rr.includes(u.id)} onChange={() => toggleRr(u.id)} data-testid={`fb-rr-${u.id}`} />
              <span>{u.name}</span>
              <span className="text-slate-500 font-mono-nums">@{u.username}</span>
            </label>
          ))}
        </div>
        <Button type="button" onClick={saveRr} className="bg-teal-600 hover:bg-teal-500" data-testid="fb-rr-save">Save round-robin group</Button>
      </Card>

      <Card className="bg-slate-900 border-slate-800 p-5 text-slate-100 space-y-4" data-testid="fb-form-import">
        <div className="font-semibold">4. Import Instant Form leads</div>
        <p className="text-sm text-slate-400">
          Facebook webhooks do not include answers. Pull every lead from the selected Instant Form, fill name / phone / email / questions, round-robin anyone new, and skip duplicates (same Facebook id, phone, or email). New webhook leads from this form stay in sync.
        </p>
        {st?.sync_form_name && (
          <p className="text-sm text-emerald-300">Syncing new leads from: {st.sync_form_name}</p>
        )}
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" className="border-slate-600" onClick={loadForms} data-testid="fb-load-forms">Load Instant Forms</Button>
        </div>
        {forms.length > 0 && (
          <div>
            <Label>Instant Form</Label>
            <select
              className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              value={formId}
              onChange={(e) => setFormId(e.target.value)}
              data-testid="fb-form-select"
            >
              <option value="">Select a form…</option>
              {forms.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name} {f.status ? `(${f.status})` : ""} {f.leads_count != null ? `· ${f.leads_count} leads` : ""}
                </option>
              ))}
            </select>
            {(forms.find((f) => f.id === formId)?.questions || []).length > 0 && (
              <p className="text-xs text-slate-500 mt-2">
                Questions: {(forms.find((f) => f.id === formId).questions || []).map((q) => q.label || q.key).join(" · ")}
              </p>
            )}
          </div>
        )}
        <Button type="button" disabled={importing || !formId} onClick={importForm} className="bg-blue-600 hover:bg-blue-500" data-testid="fb-import-form">
          {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
          Import form leads + round-robin
        </Button>
        {importResult && (
          <p className="text-sm text-slate-300" data-testid="fb-import-result">
            {importResult.form_name}: scanned {importResult.scanned}, created {importResult.created}, filled {importResult.updated}, duplicates {importResult.duplicates}, skipped {importResult.skipped}
          </p>
        )}
      </Card>

      <Card className="bg-slate-900 border-slate-800 p-5 text-slate-100 space-y-3" data-testid="fb-cleanup">
        <div className="font-semibold">5. Cleanup blank CRM cards</div>
        <p className="text-sm text-slate-400">
          Removes junk rows with no name, phone, email, or form answers (the generic “Lead” cards). Assigned real contacts are not deleted.
        </p>
        <Button type="button" variant="outline" disabled={cleaning} onClick={cleanupJunk} className="border-red-500/40 text-red-200 hover:bg-red-950/40" data-testid="fb-cleanup-junk">
          {cleaning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
          Cleanup junk leads
        </Button>
      </Card>

      <Card className="bg-slate-900 border-slate-800 p-5 text-slate-100 space-y-4" data-testid="fb-whatsapp-form">
        <div className="font-semibold flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-emerald-300" /> 6. Team WhatsApp (Cloud API)
          {st?.whatsapp?.linked && <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">Linked</Badge>}
        </div>
        <p className="text-sm text-slate-400">
          This is <strong className="text-slate-200">one Cloud API number for you, your business, and your sales team</strong> — not a product you resell to merchants. Sales logins cannot change these credentials. They send from this number, only see chats on their tag, and each person trains their own AI.
        </p>

        <div className="rounded-lg border border-slate-700 bg-slate-950/60 p-4 space-y-3 text-sm text-slate-300" data-testid="fb-wa-tutorial">
          <div className="font-semibold text-slate-100">Connect it step by step</div>
          <p className="text-xs text-amber-200/90">
            The number you register with WhatsApp Cloud API cannot stay on the consumer WhatsApp or WhatsApp Business app. Use a dedicated SIM / virtual number for the team inbox.
          </p>
          <ol className="list-decimal pl-5 text-xs text-slate-400 space-y-2.5">
            <li>
              Finish <strong className="text-slate-200">section 1</strong> first (App ID, App Secret, webhook verify token) and Save. WhatsApp uses the <em>same</em> callback URL and the <em>same</em> verify token as Facebook leads.
            </li>
            <li>
              Open the same Meta app at{" "}
              <a className="text-blue-300 underline" href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer">developers.facebook.com/apps</a>
              . Add the <strong className="text-slate-200">WhatsApp</strong> product if it is not there yet (App dashboard → Add product → WhatsApp). Switch the app to <strong className="text-slate-200">Live</strong> when you are ready for real customers (Development only talks to test numbers).
            </li>
            <li>
              WhatsApp → <strong className="text-slate-200">API Setup</strong>: pick your WhatsApp Business Account and the business phone. Copy:
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li><strong className="text-slate-200">Phone number ID</strong> (digits like 123… — this is not the +91 display number)</li>
                <li><strong className="text-slate-200">WhatsApp Business Account ID</strong> (WABA ID)</li>
              </ul>
            </li>
            <li>
              WhatsApp → <strong className="text-slate-200">Configuration</strong> (or App → Webhooks): set
              <div className="mt-2 flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" className="border-slate-600 text-slate-200 h-8" onClick={() => copy(webhook)}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy callback URL
                </Button>
                <Button type="button" size="sm" variant="outline" className="border-slate-600 text-slate-200 h-8" onClick={() => copy(verify)} disabled={!verify}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy verify token
                </Button>
              </div>
              <p className="mt-2 break-all font-mono text-[11px] text-teal-300">{webhook}</p>
              Verify token must match section 1 exactly. After Meta pings the URL you should see a green check. Subscribe webhook fields: <code className="text-teal-300">messages</code> (and optionally <code className="text-teal-300">message_status</code>).
            </li>
            <li>
              Attach the WABA to this app: Business Manager → WhatsApp Accounts → your WABA → <strong className="text-slate-200">Apps</strong> / subscribed apps. If Meta asks you to register the number for Cloud API, complete that once (you keep the same display number).
            </li>
            <li>
              Create a <strong className="text-slate-200">permanent token</strong> — do not use “Generate token” on API Setup (that token dies in ~24 hours and sending will break):
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>business.facebook.com → Business settings → <strong className="text-slate-200">Users → System users</strong></li>
                <li>Add a system user (Admin is simplest for a single company)</li>
                <li>Assign assets: this WhatsApp account <em>and</em> this Meta app</li>
                <li>Generate token for the app with at least <code className="text-teal-300">whatsapp_business_messaging</code> and <code className="text-teal-300">whatsapp_business_management</code> (add <code className="text-teal-300">business_management</code> if Meta asks)</li>
                <li>Copy the token once and paste it below. Rotate it here if it leaks.</li>
              </ul>
            </li>
            <li>
              Paste Phone number ID, token, WABA ID, and the human display number (e.g. +91…) into the form and click <strong className="text-slate-200">Save team WhatsApp</strong>. Linked should turn green.
            </li>
            <li>
              Sales panel → WhatsApp: you should see the shared number. Have a teammate message that business number from a personal phone. The chat should land on the round-robin owner&apos;s tag. Each sales user trains AI under <strong className="text-slate-200">Train your AI</strong> — that is per person, not shared.
            </li>
          </ol>
          <p className="text-xs text-slate-500">
            Official Meta docs:{" "}
            <a className="text-blue-300 underline" href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started/" target="_blank" rel="noreferrer">Cloud API get started</a>
            {" · "}
            <a className="text-blue-300 underline" href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started/add-a-phone-number" target="_blank" rel="noreferrer">Add a phone number</a>
            . If webhook verification fails, the callback must be HTTPS on this live domain and the verify token in section 1 must already be saved. If send fails with “expired token”, you used a 24-hour dashboard token — replace it with a system-user token.
          </p>
        </div>

        <div>
          <Label>Phone number ID</Label>
          <Input className="mt-1 bg-slate-950 border-slate-700" value={wa.phone_number_id} onChange={(e) => setWa({ ...wa, phone_number_id: e.target.value })} placeholder="From WhatsApp → API Setup" data-testid="fb-wa-phone-id" />
        </div>
        <div>
          <Label>Permanent access token (system user)</Label>
          <Input type="password" className="mt-1 bg-slate-950 border-slate-700" value={wa.access_token} onChange={(e) => setWa({ ...wa, access_token: e.target.value })} placeholder={st?.whatsapp?.token_set ? "saved — paste to rotate" : "System user token, not the 24h dashboard token"} data-testid="fb-wa-token" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>WABA ID</Label>
            <Input className="mt-1 bg-slate-950 border-slate-700" value={wa.waba_id} onChange={(e) => setWa({ ...wa, waba_id: e.target.value })} placeholder="WhatsApp Business Account ID" />
          </div>
          <div>
            <Label>Display phone</Label>
            <Input className="mt-1 bg-slate-950 border-slate-700" value={wa.display_phone} onChange={(e) => setWa({ ...wa, display_phone: e.target.value })} placeholder="+91…" />
          </div>
        </div>
        <Button type="button" disabled={saving} onClick={saveWa} className="bg-emerald-600 hover:bg-emerald-500" data-testid="fb-wa-save">Save team WhatsApp</Button>
      </Card>
    </div>
  );
}
